import React from "react";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

export default function SectionTitle({
  title,
  subtitle,
  align = "center", // "left" | "center"
  className = "",
  titleClassName = "",
}) {
  const isCenter = align === "center";

  return (
    <div className={cn("relative mx-auto max-w-4xl", className)}>
      {/* CSS local */}
      <style>{`
        @keyframes st-fade-up {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes st-grow {
          from { transform: scaleX(.2); opacity: 0; }
          to   { transform: scaleX(1); opacity: 1; }
        }
        @keyframes st-shimmer {
          0%   { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes st-dot {
          0%, 100% { transform: scale(1); opacity: .55; }
          50%      { transform: scale(1.35); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .st-anim { animation: none !important; transition: none !important; }
        }
      `}</style>

      <div
        className={cn(
          "st-anim",
          isCenter
            ? "text-center flex flex-col items-center"
            : "text-left flex items-start gap-4",
        )}
        style={{ animation: "st-fade-up .6s ease both" }}
      >
        {/* Accent vertical si align left */}
        {!isCenter ? (
          <span
            aria-hidden
            className={cn(
              "relative mt-1 h-10 w-[3px] rounded-full overflow-hidden",
              "bg-slate-200/80 dark:bg-slate-800",
            )}
          >
            <span
              className={cn(
                "st-anim absolute inset-0",
                "bg-[linear-gradient(180deg,transparent,rgba(59,130,246,1),transparent)]",
                "bg-[length:100%_200%]",
              )}
              style={{ animation: "st-shimmer 2.6s linear infinite" }}
            />
          </span>
        ) : null}

        <div className={cn(isCenter ? "flex flex-col items-center" : "flex-1")}>
          {/* Mini kicker discret (optionnel mais très “clean”) */}
          <div
            aria-hidden
            className={cn(
              "st-anim h-[2px] rounded-full overflow-hidden",
              isCenter ? "mx-auto w-10" : "w-10",
              "bg-slate-200/80 dark:bg-slate-800",
            )}
            style={{ animation: "st-grow .7s ease both .05s", transformOrigin: "left" }}
          >
            <div
              className={cn(
                "h-full w-full",
                "bg-[linear-gradient(90deg,transparent,rgba(59,130,246,.95),transparent)]",
                "bg-[length:200%_100%]",
              )}
              style={{ animation: "st-shimmer 3.2s linear infinite" }}
            />
          </div>

          <h2
            className={cn(
              "mt-4 text-3xl md:text-[36px] font-extrabold tracking-tight",
              "text-slate-900 dark:text-slate-50",
              titleClassName
            )}
          >
            {title}
          </h2>

          {/* Underline capsule + dots (remplace les 4 traits) */}
          <div
            aria-hidden
            className={cn(
              "st-anim mt-3 flex items-center gap-3",
              isCenter ? "justify-center" : "justify-start",
            )}
            style={{ animation: "st-fade-up .6s ease both .08s" }}
          >
            <span
              className={cn(
                "st-anim h-2 w-2 rounded-full",
                "bg-primary/70",
              )}
              style={{ animation: "st-dot 1.8s ease-in-out infinite" }}
            />
            <span
              className={cn(
                "relative h-[3px] w-24 md:w-32 rounded-full overflow-hidden",
                "bg-slate-200/80 dark:bg-slate-800",
              )}
            >
              <span
                className={cn(
                  "st-anim absolute inset-0",
                  "bg-[linear-gradient(90deg,transparent,rgba(59,130,246,1),transparent)]",
                  "bg-[length:200%_100%]",
                )}
                style={{ animation: "st-shimmer 2.6s linear infinite" }}
              />
            </span>
            <span
              className={cn(
                "st-anim h-2 w-2 rounded-full",
                "bg-primary/70",
              )}
              style={{ animation: "st-dot 1.8s ease-in-out infinite .35s" }}
            />
          </div>

          {subtitle ? (
            <p
              className={cn(
                "st-anim mt-3 text-sm md:text-base",
                "text-slate-600 dark:text-slate-300",
                isCenter ? "max-w-2xl" : "max-w-3xl",
              )}
              style={{ animation: "st-fade-up .6s ease both .14s" }}
            >
              {subtitle}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
